/**
 * Memory store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { accountApi } from '@/lib/api/account';

const getStores = () => require('@/store');

const useMemoryStore = create((set, get) => ({
  // State
  memories: {},      // Map of memories by id
  memoryIds: [],     // Ordered list of memory ids
  // Includes collection changes, which are deliberately absent from the account memory map.
  workStarterPromptsRevision: 0,
  workStarterPromptsChangedAt: 0,
  invalidateWorkStarterPrompts: () => set((state) => ({
    workStarterPromptsRevision: state.workStarterPromptsRevision + 1,
    workStarterPromptsChangedAt: Date.now(),
  })),
  isLoading: {
    fetch: false,
    create: false,
    update: false,
    delete: false,
  },
  errors: {
    fetch: null,
    create: null,
    update: null,
    delete: null,
  },

  // Helpers
  addMemories: (newMemories) => {
    if (!Array.isArray(newMemories)) {
      newMemories = [newMemories];
    }

    const memoriesMap = {};
    const newIds = [];

    newMemories.forEach((memory) => {
      if (!memory || !memory.id) return;
      memoriesMap[memory.id] = memory;
      newIds.push(memory.id);
    });

    set((state) => ({
      memories: {
        ...state.memories,
        ...memoriesMap,
      },
      memoryIds: [...new Set([...newIds, ...state.memoryIds])], // New memories first
    }));

    return newMemories;
  },

  // Add or update a single memory (helper for socket events - no API call).
  // Upserts because memory_updated events target memories already in the map;
  // the entry moves to the front so recent activity surfaces first.
  addMemory: (memory) => {
    if (!memory || !memory.id) return;

    set((state) => ({
      memories: {
        ...state.memories,
        [memory.id]: state.memories[memory.id]
          ? { ...state.memories[memory.id], ...memory }
          : memory,
      },
      memoryIds: [memory.id, ...state.memoryIds.filter(id => id !== memory.id)],
    }));
  },

  // Remove a memory (helper for socket events - no API call)
  removeMemory: (memoryId) => {
    if (!memoryId) return;
    
    set((state) => ({
      memories: Object.fromEntries(
        Object.entries(state.memories).filter(([id]) => id !== memoryId)
      ),
      memoryIds: state.memoryIds.filter((id) => id !== memoryId),
    }));
  },

  // Actions
  fetchMemories: async () => {
    if (!getStores().useAuthStore.getState().token) {
      set((state) => ({
        isLoading: { ...state.isLoading, fetch: false },
        errors: { ...state.errors, fetch: null },
      }));
      return [];
    }
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, fetch: true },
        errors: { ...state.errors, fetch: null },
      }));

      const response = await accountApi.getMemories();
      const memories = response.memories || [];

      // Build map and ids
      const memoriesMap = {};
      const memoryIds = [];
      memories.forEach((memory) => {
        if (memory && memory.id) {
          memoriesMap[memory.id] = memory;
          memoryIds.push(memory.id);
        }
      });

      set((state) => ({
        memories: memoriesMap,
        memoryIds,
        isLoading: { ...state.isLoading, fetch: false },
        errors: { ...state.errors, fetch: null },
      }));

      return memories;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          fetch: error.message || 'Failed to fetch memories',
        },
        isLoading: { ...state.isLoading, fetch: false },
      }));
      throw error;
    }
  },

  createMemory: async (content) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, create: true },
        errors: { ...state.errors, create: null },
      }));

      const response = await accountApi.createMemory(content);
      const memory = response?.memory;
      if (!memory?.id) throw new Error('The memory could not be saved. Please try again.');

      if (memory) {
        set((state) => ({
          memories: {
            ...state.memories,
            [memory.id]: memory,
          },
          memoryIds: [memory.id, ...state.memoryIds.filter(id => id !== memory.id)],
          isLoading: { ...state.isLoading, create: false },
        }));
      }

      return memory;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          create: error.message || 'Failed to create memory',
        },
        isLoading: { ...state.isLoading, create: false },
      }));
      throw error;
    }
  },

  // Batch create used by the Import flow. One HTTP round-trip, one
  // embedding batch on the server, so importing 100 memories doesn't
  // fire 100 sequential embedding API calls.
  bulkCreateMemories: async (contents) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, create: true },
        errors: { ...state.errors, create: null },
      }));

      const response = await accountApi.bulkCreateMemories(contents);
      const created = Array.isArray(response?.created) ? response.created : [];

      if (created.length > 0) {
        const newMap = {};
        const newIds = [];
        for (const memory of created) {
          if (memory && memory.id) {
            newMap[memory.id] = memory;
            newIds.push(memory.id);
          }
        }
        set((state) => ({
          memories: { ...state.memories, ...newMap },
          memoryIds: [...new Set([...newIds, ...state.memoryIds])],
          isLoading: { ...state.isLoading, create: false },
        }));
      } else {
        set((state) => ({
          isLoading: { ...state.isLoading, create: false },
        }));
      }

      return { created, skipped: response?.skipped || [] };
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          create: error.message || 'Failed to bulk create memories',
        },
        isLoading: { ...state.isLoading, create: false },
      }));
      throw error;
    }
  },

  updateMemory: async (memoryId, content) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, update: true },
        errors: { ...state.errors, update: null },
      }));

      const response = await accountApi.updateMemory(memoryId, content);
      const memory = response?.memory;
      if (!memory?.id) throw new Error('The memory could not be saved. Please try again.');

      if (memory) {
        set((state) => ({
          memories: {
            ...state.memories,
            [memory.id]: memory,
          },
          isLoading: { ...state.isLoading, update: false },
        }));
      }

      return memory;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          update: error.message || 'Failed to update memory',
        },
        isLoading: { ...state.isLoading, update: false },
      }));
      throw error;
    }
  },

  deleteMemory: async (memoryId) => {
    try {
      set((state) => ({
        isLoading: { ...state.isLoading, delete: true },
        errors: { ...state.errors, delete: null },
      }));

      await accountApi.deleteMemory(memoryId);

      set((state) => ({
        memories: Object.fromEntries(
          Object.entries(state.memories).filter(([id]) => id !== memoryId)
        ),
        memoryIds: state.memoryIds.filter((id) => id !== memoryId),
        isLoading: { ...state.isLoading, delete: false },
      }));

      return true;
    } catch (error) {
      set((state) => ({
        errors: {
          ...state.errors,
          delete: error.message || 'Failed to delete memory',
        },
        isLoading: { ...state.isLoading, delete: false },
      }));
      throw error;
    }
  },

  // Selectors
  getMemoryById: (id) => get().memories[id],

  getAllMemories: () => {
    const state = get();
    return state.memoryIds.map((id) => state.memories[id]).filter(Boolean);
  },

  // Reset
  clearMemories: () => {
    set({
      memories: {},
      memoryIds: [],
      isLoading: {
        fetch: false,
        create: false,
        update: false,
        delete: false,
      },
      errors: {
        fetch: null,
        create: null,
        update: null,
        delete: null,
      },
    });
  },
}));

export { useMemoryStore };
export default useMemoryStore;
